/* ═══════════════════════════════════════════════════════════════════════════════
   Progress Claims Tab Tour (v2.4.0)

   Feature update tour for the Progress Claims grid (ProgressRangeView).
   Focuses on:
   - Year/range controls for navigating periods
   - Category and subcategory row structure
   - Click-to-edit cells (%, $, status)
   - NEW: Edit modal's payment section (claimed vs paid)
   - NEW: Split cell display (Claimed top / Paid bottom)
   - Connection to Claims Report → Add Payment → Progress Payment

   Steps 4-6 use 'welcome'-type centered cards with inline sample visuals
   because the edit modal and split cells may not be in the DOM at tour time
   (no payments recorded yet, or modal is closed).
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const progressClaimsTour: TourDefinition = {
  id: 'progress_claims_v2_4',
  version: '2.4.0',
  type: 'feature_update',
  audience: 'all',
  page: 'progress_claims',
  priority: 2,
  title: 'Progress Claims',
  subtitle: 'Track, reconcile, and visualise claim progress at the subcategory level.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'Progress Claims: What\'s New',
      description: 'Your progress claims grid just got smarter. Cells now show payment reconciliation status, and clicking any cell reveals a detailed breakdown of claimed vs paid. Let\'s walk through everything.',
      icon: '📊',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Year & Range Controls ────────────────────────────────
    {
      id: 'year-controls',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pc-year-controls"]',
      position: 'bottom',
      title: 'Navigate Time Periods',
      description: 'Use the year buttons to quickly jump to a full year, or use the dropdowns to set a custom month range. The grid scrolls horizontally, so use the arrow buttons on the right to navigate.',
      icon: '📅',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 2: Category Row ─────────────────────────────────────────
    {
      id: 'category-row',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pc-category-row"]',
      position: 'bottom',
      title: 'Category Breakdown',
      description: 'Each coloured row is a contract category. It shows the category name, total contract value, cumulative claim percentage, and total claimed dollar amount. Subcategories are listed below, each with their own row.',
      icon: '📁',
      actionLabel: 'Next',
      spotlightPadding: 6,
    },

    // ── Step 3: Click a Cell ─────────────────────────────────────────
    {
      id: 'cell-click',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pc-first-cell"]',
      position: 'bottom',
      title: 'Click Any Cell to Edit',
      description: 'Click any cell in the grid to enter or update the claim percentage for that subcategory and month. You can toggle between percentage (%) and dollar ($) input modes.',
      icon: '✏️',
      actionLabel: 'Next',
      spotlightPadding: 10,
    },

    // ── Step 4: Edit Modal (Sample Card) ─────────────────────────────
    // Uses welcome-type card because the modal isn't open during the tour
    {
      id: 'edit-modal-sample',
      type: 'info_card',
      title: 'Edit Cell: What You\'ll See',
      description: 'When you click a cell, a modal opens with:\n\n📐 Toggle: Switch between % and $ input\n📊 Status Card: Shows contract value vs total claimed\n💰 Payment Section: Shows if this item was Paid, Voided, Agreed, or Pushed from Claims Report\n\nThe payment section only appears after you record a payment via Claims Report → Add Payment.',
      icon: '🔍',
      actionLabel: 'Next',
    },

    // ── Step 5: Split Cell (Sample Card) ─────────────────────────────
    // No spotlight because split cells only exist after payments are recorded
    {
      id: 'split-cell-sample',
      type: 'info_card',
      title: 'Split Cells: Claimed vs Paid',
      description: 'After payment is recorded, cells transform into a split indicator:\n\n🟢 Top half (C): Your claimed percentage\n🔵 Bottom half (P): The paid percentage or action\n\nActions are colour-coded:\n• ✅ Green = Paid in full\n• ✎ Blue = Agreement Made (partial)\n• ✕ Red = Voided\n• ⏭ Orange = Works Not Completed (pushed)',
      icon: '📐',
      actionLabel: 'Next',
    },

    // ── Step 6: Connection to Claims Report ──────────────────────────
    {
      id: 'connection-note',
      type: 'info_card',
      title: 'Connected to Claims Report',
      description: 'To record payments that power the split cells:\n\n1️⃣ Go to the Claims Report tab\n2️⃣ Click "Paid by Builder" on any claim row\n3️⃣ Choose "Progress Payment"\n4️⃣ Set the action for each subcategory\n5️⃣ Save, and the progress grid updates automatically\n\nPayment data flows both ways. Changes here are reflected in the Claims Report and vice versa.',
      icon: '🔗',
      actionLabel: 'Next',
    },

    // ── Step 7: Category Legend ──────────────────────────────────────
    {
      id: 'category-legend',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pc-category-legend"]',
      position: 'top',
      title: 'Quick Category Jump',
      description: 'Click any category dot in this legend to instantly scroll to that section in the grid. The active category is highlighted as you scroll.',
      icon: '🎯',
      actionLabel: 'Got It',
      spotlightPadding: 8,
    },

    // ── Step 8: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'You\'re Ready!',
      description: 'Click any cell to start entering claims. Once payments are recorded via Claims Report, you\'ll see the split cell indicators right here in the grid. The edit modal will show the full reconciliation status for each item.',
      icon: '🎉',
      actionLabel: 'Start Claiming',
    },
  ],
};
